import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import { Button } from '../components/ui/Button';
import { AuthCore } from '../core/AuthCore';
import { RefreshCw, FileText, RotateCcw, ArrowLeft, Loader2 } from 'lucide-react';

interface ActivityItem {
  id: string;
  type: 'album_creation' | 'regen_metadata';
  title: string;
  description: string;
  creditCost: number;
  albumId?: number;
  albumTitle?: string;
  albumSlug?: string;
  fileName?: string;
  assetCount?: number;
  timestamp: string;
}

const AllActivityPage: React.FC = () => {
  const navigate = useNavigate();
  const [activities, setActivities] = useState<ActivityItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  const limit = 20; // Load 20 items per page

  useEffect(() => {
    fetchActivities(true);
  }, []);

  const fetchActivities = async (reset = false) => {
    try {
      if (reset) {
        setIsLoading(true);
        setError(null);
        setPage(0);
      } else {
        setIsLoadingMore(true);
      }

      const token = await AuthCore.getInstance().getAccessToken();
      if (!token) {
        throw new Error('No authentication token available');
      }

      const currentPage = reset ? 0 : page;
      const offset = currentPage * limit;

      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'https://api.metapic.app'}/credits/recent-history?limit=${limit}&offset=${offset}&all=true`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch activities: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        const newActivities = data.data.activities;
        
        if (reset) {
          setActivities(newActivities);
        } else {
          setActivities(prev => [...prev, ...newActivities]);
        }
        
        setHasMore(data.data.pagination?.hasMore || false);
        setTotalCount(data.data.total || newActivities.length);
        setPage(currentPage + 1);
      } else {
        throw new Error(data.error || 'Failed to load activities');
      }
    } catch (err) {
      console.error('❌ Failed to fetch activities:', err);
      setError(err instanceof Error ? err.message : 'Failed to load activities');
      if (reset) {
        setActivities([]);
      }
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) {
      return 'Just now';
    } else if (diffInHours < 24) {
      return `${diffInHours}h ago`;
    } else {
      const diffInDays = Math.floor(diffInHours / 24);
      return `${diffInDays}d ago`;
    }
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'album_creation':
        return <FileText className="w-5 h-5 text-blue-600" />;
      case 'regen_metadata':
        return <RotateCcw className="w-5 h-5 text-orange-600" />;
      default:
        return <FileText className="w-5 h-5 text-gray-600" />;
    }
  };

  const getActivityColor = (type: string) => {
    switch (type) {
      case 'album_creation':
        return 'bg-blue-50 border-blue-200 text-blue-800';
      case 'regen_metadata':
        return 'bg-orange-50 border-orange-200 text-orange-800';
      default:
        return 'bg-gray-50 border-gray-200 text-gray-800';
    }
  };

  const handleLoadMore = () => {
    fetchActivities(false);
  };

  const handleRefresh = () => {
    fetchActivities(true);
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
          <div className="container mx-auto px-4 py-8 max-w-4xl">
            <div className="flex items-center justify-center py-20">
              <div className="text-center">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-4" />
                <p className="text-gray-600">Loading activities...</p>
              </div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
        <div className="container mx-auto px-4 py-8 max-w-4xl">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/my-account')}
                  className="flex items-center space-x-2"
                >
                  <ArrowLeft className="w-4 h-4" />
                  <span>Back to Account</span>
                </Button>
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">All Activity</h1>
                  <p className="text-gray-600">Complete credit usage history</p>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <div className="text-sm text-gray-500">
                  {totalCount > 0 && `${activities.length} of ${totalCount} activities`}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRefresh}
                  className="flex items-center space-x-2"
                >
                  <RefreshCw className="w-4 h-4" />
                  <span>Refresh</span>
                </Button>
              </div>
            </div>
          </div>

          {/* Error State */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center">
                <div className="text-red-400 mr-3">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <p className="text-red-800 font-medium">Failed to load activities</p>
                  <p className="text-red-600 text-sm">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Activities List */}
          {activities.length === 0 ? (
            <div className="text-center py-20">
              <div className="text-gray-400 mb-4">
                <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No activities found</h3>
              <p className="text-gray-500 mb-6">Your credit usage will appear here</p>
              <Button onClick={handleRefresh} variant="outline">
                Try Again
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {activities.map((activity) => (
                <div 
                  key={activity.id} 
                  className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-4 min-w-0 flex-1">
                      <div className="flex-shrink-0 mt-1">
                        {getActivityIcon(activity.type)}
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="text-lg font-medium text-gray-900 mb-1">
                          {activity.albumSlug ? (
                            <a 
                              href={`/results-v2/${activity.albumSlug}`}
                              className="text-blue-600 hover:text-blue-800 hover:underline"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {activity.title}
                            </a>
                          ) : (
                            activity.title
                          )}
                        </h3>
                        <p className="text-gray-600 mb-2">
                          {activity.description}
                        </p>
                        <div className="flex items-center space-x-4 text-sm text-gray-500">
                          <span>{formatDate(activity.timestamp)}</span>
                          {activity.albumTitle && (
                            <span>• {activity.albumTitle}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex-shrink-0 ml-4">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getActivityColor(activity.type)}`}>
                        {activity.creditCost} credit{activity.creditCost > 1 ? 's' : ''}
                      </span>
                    </div>
                  </div>
                </div>
              ))}

              {/* Load More Button */}
              {hasMore && (
                <div className="text-center pt-6">
                  <Button
                    onClick={handleLoadMore}
                    disabled={isLoadingMore}
                    variant="outline"
                    className="px-8"
                  >
                    {isLoadingMore ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Loading...
                      </>
                    ) : (
                      'Load More Activities'
                    )}
                  </Button>
                </div>
              )}

              {/* End of List */}
              {!hasMore && activities.length > 0 && (
                <div className="text-center pt-6">
                  <p className="text-gray-500 text-sm">You've reached the end of your activity history</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default AllActivityPage;
